import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchYoutubeOverview, fetchYoutubeVideos, fetchYoutubeDemographics, formatNumber } from "@/lib/api";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import {
  Youtube, Users, Eye, Video, ThumbsUp, MessageSquare, Share2, TrendingUp, TrendingDown,
  DollarSign, Calendar, ExternalLink, PlayCircle, Globe2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const RANGE_OPTIONS = [
  { value: 7, label: "7 días" },
  { value: 30, label: "30 días" },
  { value: 90, label: "90 días" },
  { value: 365, label: "12 meses" },
];

function getDateRange(days) {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - days);
  const iso = (d) => d.toISOString().split("T")[0];
  return { date_from: iso(from), date_to: iso(today) };
}

function fmt(n) {
  if (n === null || n === undefined) return "0";
  return formatNumber ? formatNumber(n) : Number(n).toLocaleString("es-AR");
}

function fmtMoney(n) {
  if (!n) return "$0";
  return "$" + Number(n).toLocaleString("es-AR", { maximumFractionDigits: 2 });
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
  } catch {
    return iso.slice(0, 10);
  }
}

function fmtDuration(seconds) {
  if (!seconds) return "—";
  const s = Math.round(Number(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export default function YoutubeSection({ clientId }) {
  const [rangeDays, setRangeDays] = useState(30);
  const [sortBy, setSortBy] = useState("views");
  const range = useMemo(() => getDateRange(rangeDays), [rangeDays]);

  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ["yt-overview", clientId, range.date_from, range.date_to],
    queryFn: () => fetchYoutubeOverview(clientId, range.date_from, range.date_to),
    staleTime: 5 * 60 * 1000,
  });

  const { data: videosData, isLoading: loadingVideos } = useQuery({
    queryKey: ["yt-videos", clientId, range.date_from, range.date_to],
    queryFn: () => fetchYoutubeVideos(clientId, range.date_from, range.date_to, "all"),
    enabled: !!overview?.configured,
    staleTime: 5 * 60 * 1000,
  });

  const { data: demographics } = useQuery({
    queryKey: ["yt-demographics", clientId],
    queryFn: () => fetchYoutubeDemographics(clientId),
    enabled: !!overview?.configured,
    staleTime: 30 * 60 * 1000,
  });

  // Build combined timeline (subscribers + views) from first channel for chart.
  // Must be declared BEFORE any early-return to comply with React hooks rules.
  const primaryChannel = overview?.channels?.[0];
  const chartData = useMemo(() => {
    const subsTimeline = primaryChannel?.timelines?.subscribers || [];
    const viewsTimeline = primaryChannel?.timelines?.views || [];
    const byDate = {};
    subsTimeline.forEach((p) => {
      const d = (p.dateTime || "").slice(0, 10);
      if (!d) return;
      byDate[d] = byDate[d] || { date: d };
      byDate[d].subscribers = Number(p.value || 0);
    });
    viewsTimeline.forEach((p) => {
      const d = (p.dateTime || "").slice(0, 10);
      if (!d) return;
      byDate[d] = byDate[d] || { date: d };
      byDate[d].views = Number(p.value || 0);
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [primaryChannel]);

  if (loadingOverview) {
    return (
      <div className="space-y-4" data-testid="yt-loading">
        <div className="h-24 rounded-md skeleton" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-24 rounded-md skeleton" />)}
        </div>
        <div className="h-72 rounded-md skeleton" />
      </div>
    );
  }

  if (!overview?.configured) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center" data-testid="yt-not-configured">
        <Youtube className="h-10 w-10 text-slate-400 mx-auto mb-3" />
        <h3 className="font-display text-base font-semibold text-slate-900">YouTube no configurado</h3>
        <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
          {overview?.message || "Este cliente no tiene un canal de YouTube configurado en Metricool. Asociá un canal en la marca de Metricool para empezar a verlo acá."}
        </p>
      </div>
    );
  }

  const totals = overview.totals || {};
  const channels = overview.channels || [];
  const allVideos = videosData?.videos || [];
  const videos = [...allVideos].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));

  return (
    <div className="space-y-5" data-testid="yt-section">
      {/* Header + range selector */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md bg-red-50 border border-red-100 flex items-center justify-center">
            <Youtube className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900 leading-tight">YouTube</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {channels.length === 1 ? channels[0].label : `${channels.length} canales`} · datos vía Metricool
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md p-0.5" data-testid="yt-range-selector">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setRangeDays(opt.value)}
              data-testid={`yt-range-${opt.value}`}
              className={`px-2.5 py-1 text-xs font-medium rounded-sm transition ${
                rangeDays === opt.value
                  ? "bg-white shadow-sm text-slate-900"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Comunidad KPIs */}
      <section data-testid="yt-community">
        <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Comunidad</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
          <KpiCard icon={Users} label="Suscriptores" value={fmt(totals.subscribers)} tone="red" testid="yt-kpi-subs" />
          <KpiCard icon={Eye} label="Vistas (canal)" value={fmt(totals.views)} tone="blue" testid="yt-kpi-views" />
          <KpiCard icon={Video} label="Videos totales" value={fmt(totals.videos)} tone="amber" testid="yt-kpi-videos" />
          <KpiCard icon={TrendingUp} label="Ganados" value={fmt(totals.gained)} tone="emerald" testid="yt-kpi-gained" />
          <KpiCard icon={TrendingDown} label="Perdidos" value={fmt(totals.lost)} tone="rose" testid="yt-kpi-lost" />
          <KpiCard icon={DollarSign} label="Ingresos est." value={fmtMoney(totals.revenue)} tone="green" testid="yt-kpi-revenue" />
        </div>
      </section>

      {/* Engagement de videos en el rango */}
      <section data-testid="yt-engagement">
        <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Engagement de videos · período</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
          <KpiCard icon={PlayCircle} label="Reproducciones" value={fmt(totals.video_views)} tone="red" testid="yt-vkpi-views" />
          <KpiCard icon={ThumbsUp} label="Likes" value={fmt(totals.likes)} tone="emerald" testid="yt-vkpi-likes" />
          <KpiCard icon={ThumbsUp} label="Dislikes" value={fmt(totals.dislikes)} tone="slate" rotate testid="yt-vkpi-dislikes" />
          <KpiCard icon={MessageSquare} label="Comentarios" value={fmt(totals.comments)} tone="sky" testid="yt-vkpi-comments" />
          <KpiCard icon={Share2} label="Compartidos" value={fmt(totals.shares)} tone="indigo" testid="yt-vkpi-shares" />
        </div>
      </section>

      {/* Evolution chart */}
      {chartData.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-md p-4" data-testid="yt-evolution-chart">
          <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Evolución · {primaryChannel?.label}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="ytSubsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#dc2626" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#dc2626" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={fmtDateShort} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip
                contentStyle={{ background: "#0f172a", border: "none", borderRadius: 6, fontSize: 12 }}
                labelStyle={{ color: "#e2e8f0" }}
                itemStyle={{ color: "#fff" }}
              />
              <Area type="monotone" dataKey="subscribers" name="Suscriptores" stroke="#dc2626" fill="url(#ytSubsGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="views" name="Vistas" stroke="#0284c7" fill="transparent" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* Per-channel cards (if multiple) */}
      {channels.length > 1 && (
        <section data-testid="yt-channels-grid">
          <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Por canal</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {channels.map((ch) => (
              <div key={ch.blog_id} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`yt-channel-${ch.blog_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  {ch.picture && <img src={ch.picture} alt="" className="w-7 h-7 rounded-full object-cover" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{ch.label}</div>
                    {ch.youtube_url && (
                      <a
                        href={`https://www.youtube.com/channel/${ch.youtube_url}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-[11px] text-red-600 hover:underline flex items-center gap-1"
                      >
                        Ver canal <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-base font-semibold text-slate-900">{fmt(ch.subscribers)}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider">Subs</div></div>
                  <div><div className="text-base font-semibold text-slate-900">{fmt(ch.views)}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider">Vistas</div></div>
                  <div><div className="text-base font-semibold text-slate-900">{fmt(ch.videos)}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider">Videos</div></div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Videos table */}
      <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="yt-videos-section">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Lista de videos</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">{videos.length} videos con métricas en el período</p>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md p-0.5">
            {[
              { v: "views", l: "Vistas" },
              { v: "likes", l: "Likes" },
              { v: "comments", l: "Coment." },
              { v: "shares", l: "Comp." },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setSortBy(opt.v)}
                data-testid={`yt-sort-${opt.v}`}
                className={`px-2.5 py-1 text-xs font-medium rounded-sm transition ${
                  sortBy === opt.v
                    ? "bg-white shadow-sm text-slate-900"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        {loadingVideos ? (
          <div className="p-6 space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-md skeleton" />)}</div>
        ) : videos.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="yt-videos-empty">Sin videos en el período</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="yt-videos-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-medium">Video</th>
                  <th className="px-3 py-2.5 font-medium">Tipo</th>
                  <th className="px-3 py-2.5 font-medium">Publicado</th>
                  <th className="px-3 py-2.5 font-medium text-right">Vistas</th>
                  <th className="px-3 py-2.5 font-medium text-right">Min vistos</th>
                  <th className="px-3 py-2.5 font-medium text-right">Dur. prom.</th>
                  <th className="px-3 py-2.5 font-medium text-right">Likes</th>
                  <th className="px-3 py-2.5 font-medium text-right">Coment.</th>
                  <th className="px-3 py-2.5 font-medium text-right">Comp.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {videos.map((v) => (
                  <tr key={v.video_id} className="hover:bg-slate-50" data-testid={`yt-video-row-${v.video_id}`}>
                    <td className="px-4 py-2.5">
                      <a href={v.watch_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group">
                        {v.thumbnail_url ? (
                          <img src={v.thumbnail_url} alt="" className="w-16 h-9 object-cover rounded-sm flex-shrink-0" />
                        ) : (
                          <div className="w-16 h-9 bg-slate-200 rounded-sm flex-shrink-0" />
                        )}
                        <span className="text-slate-900 group-hover:text-red-600 line-clamp-2 max-w-[280px]">{v.title}</span>
                      </a>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        v.video_type === "SHORT" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                        "bg-sky-50 text-sky-700 border border-sky-200"
                      }`}>
                        {v.video_type === "SHORT" ? "Short" : "Video"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmtDateShort(v.published_at)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-900 tabular-nums">{fmt(v.views)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(Math.round(v.watch_minutes))}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmtDuration(v.avg_view_duration)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(v.likes)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(v.comments)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(v.shares)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Demographics */}
      <DemographicsBlock data={demographics} />
    </div>
  );
}

/* ─────────────── Helpers ─────────────── */

function KpiCard({ icon: Icon, label, value, tone = "slate", rotate = false, testid }) {
  const tones = {
    red: { bg: "bg-red-50", border: "border-red-100", text: "text-red-600" },
    blue: { bg: "bg-blue-50", border: "border-blue-100", text: "text-blue-600" },
    sky: { bg: "bg-sky-50", border: "border-sky-100", text: "text-sky-600" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-100", text: "text-emerald-600" },
    green: { bg: "bg-emerald-50", border: "border-emerald-100", text: "text-emerald-700" },
    amber: { bg: "bg-amber-50", border: "border-amber-100", text: "text-amber-600" },
    rose: { bg: "bg-rose-50", border: "border-rose-100", text: "text-rose-600" },
    indigo: { bg: "bg-indigo-50", border: "border-indigo-100", text: "text-indigo-600" },
    slate: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-500" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={testid}>
      <div className={`w-7 h-7 rounded-md ${t.bg} border ${t.border} flex items-center justify-center mb-1.5`}>
        <Icon className={`h-3.5 w-3.5 ${t.text} ${rotate ? "rotate-180" : ""}`} />
      </div>
      <div className="text-lg font-display font-semibold text-slate-900 leading-tight tabular-nums">{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function DemographicsBlock({ data }) {
  if (!data?.configured || !data.channels?.length) return null;
  const merged = mergeDemographics(data.channels.map((c) => c.demographics));
  const hasAny = Object.values(merged).some((arr) => arr.length > 0);
  if (!hasAny) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-md p-4" data-testid="yt-demographics">
      <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
        <Globe2 className="h-3.5 w-3.5" /> Demografía de la audiencia
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <DemoList title="Género" rows={merged.gender} />
        <DemoList title="Edad" rows={merged.age} />
        <DemoList title="País (Top 10)" rows={merged.country.slice(0, 10)} />
        <DemoList title="Fuente de tráfico" rows={merged.traffic_source.slice(0, 10)} />
      </div>
    </section>
  );
}

function mergeDemographics(list) {
  const out = { gender: [], age: [], country: [], traffic_source: [] };
  for (const dem of list) {
    if (!dem) continue;
    for (const k of Object.keys(out)) {
      const items = dem[k] || [];
      out[k].push(...items);
    }
  }
  // De-duplicate by label, summing values
  for (const k of Object.keys(out)) {
    const agg = {};
    for (const item of out[k]) {
      const lbl = item.label || item.name || "(?)";
      const val = Number(item.value || item.percent || 0);
      agg[lbl] = (agg[lbl] || 0) + val;
    }
    out[k] = Object.entries(agg)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }
  return out;
}

function DemoList({ title, rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div>
        <h4 className="text-[11px] font-semibold text-slate-700 mb-2">{title}</h4>
        <p className="text-xs text-slate-400">Sin datos</p>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div>
      <h4 className="text-[11px] font-semibold text-slate-700 mb-2">{title}</h4>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.label} className="text-xs">
            <div className="flex justify-between mb-0.5">
              <span className="text-slate-700 truncate pr-2">{r.label}</span>
              <span className="text-slate-900 font-medium tabular-nums">{r.value.toFixed(1)}%</span>
            </div>
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-red-500 rounded-full" style={{ width: `${(r.value / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
